import { mean, stdDev } from './aggregations';

export const schoolMonthIndex = (dateInput) => {
  const date = new Date(dateInput);
  const month = date.getMonth() + 1;
  if (month >= 9) return month - 9;
  return month + 3;
};

export const linearRegression = (values) => {
  const points = (values || []).map((value, index) =>
    typeof value === 'number' ? { x: index, y: value } : { x: value.x, y: value.y },
  );
  const n = points.length;
  if (n < 2) {
    return { slope: 0, intercept: points[0]?.y || 0, r2: 0 };
  }
  const sumX = points.reduce((sum, point) => sum + point.x, 0);
  const sumY = points.reduce((sum, point) => sum + point.y, 0);
  const sumXY = points.reduce((sum, point) => sum + point.x * point.y, 0);
  const sumXX = points.reduce((sum, point) => sum + point.x * point.x, 0);
  const denominator = n * sumXX - sumX * sumX;
  const slope = denominator === 0 ? 0 : (n * sumXY - sumX * sumY) / denominator;
  const intercept = (sumY - slope * sumX) / n;

  const meanY = sumY / n;
  const ssTot = points.reduce((sum, point) => sum + (point.y - meanY) ** 2, 0);
  const ssRes = points.reduce((sum, point) => {
    const predicted = slope * point.x + intercept;
    return sum + (point.y - predicted) ** 2;
  }, 0);
  const r2 = ssTot === 0 ? 0 : 1 - ssRes / ssTot;

  return { slope, intercept, r2 };
};

export const movingAverage = (series, window = 3) =>
  (series || []).map((_, index) => {
    const start = Math.max(0, index - window + 1);
    return mean(series.slice(start, index + 1));
  });

const erf = (x) => {
  const sign = x < 0 ? -1 : 1;
  const absolute = Math.abs(x);
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;
  const t = 1 / (1 + p * absolute);
  const poly = ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t;
  const value = 1 - poly * Math.exp(-absolute * absolute);
  return sign * value;
};

export const normalCdf = (z) => 0.5 * (1 + erf(z / Math.SQRT2));

export const forecastEndOfYear = (monthlyCumulative, targetCredits, currentMonthIndex) => {
  const cumulative = monthlyCumulative || [];
  const index = Math.min(9, Math.max(0, currentMonthIndex ?? 9));
  const current = cumulative[index] || 0;
  const monthsLeft = Math.max(0, 9 - index);
  const { slope } = linearRegression(cumulative.slice(0, index + 1));
  const estimated = Math.max(current, current + slope * monthsLeft);

  const monthly = cumulative.slice(1).map((value, position) => value - cumulative[position]);
  const sigma = stdDev(monthly.slice(0, index)) || 0;
  const needed = Math.max(0, (targetCredits || 0) - current);
  const expectedGain = Math.max(0, slope * monthsLeft);
  const denominator = Math.max(1, sigma * Math.sqrt(Math.max(1, monthsLeft)));
  const probability = !targetCredits || targetCredits <= 0
    ? 1
    : normalCdf((expectedGain - needed) / denominator);

  return { estimated, probability, monthsLeft, expectedGain, needed, sigma };
};

export const daysToTarget = (current, target, dailyRate) => {
  if (!dailyRate || dailyRate <= 0) return null;
  const remaining = (target || 0) - (current || 0);
  if (remaining <= 0) return 0;
  return Math.ceil(remaining / dailyRate);
};
